import { API_ENDPOINTS } from '@/config/api';
import { apiClient } from '@/services/api/client';
import type {
  BackendApiResponse,
  EnquiryListResponse,
  GeneralEnquiry,
} from '@/types/general-enquiry';

export const ENQUIRY_MODULE = {
  PROPERTY_ENQUIRY: 'Property Enquiry',
  GENERAL_ENQUIRY: 'General Enquiry',
} as const;

export type EnquiryModuleType = (typeof ENQUIRY_MODULE)[keyof typeof ENQUIRY_MODULE];

export type GeneralEnquiryExportFormat = 'excel' | 'csv' | 'pdf';

export interface GeneralEnquiryListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?:
    | 'id'
    | 'full_name'
    | 'email'
    | 'message'
    | 'status'
    | 'phone'
    | 'assigned_to'
    | 'created_at'
    | 'updated_at'
    | 'subscribeToUpdates'
    | 'agree_to_terms'
    | 'property_id';
  sortOrder?: 'ASC' | 'DESC';
  /** ISO date (YYYY-MM-DD) inclusive lower bound on created_at. */
  dateFrom?: string;
  /** ISO date (YYYY-MM-DD) inclusive upper bound on created_at. */
  dateTo?: string;
  module?: string;
}

export interface GeneralEnquiryExportParams {
  type: GeneralEnquiryExportFormat;
  search?: string;
  status?: string;
  assignedTo?: number;
  sortBy?: GeneralEnquiryListParams['sortBy'];
  sortOrder?: 'ASC' | 'DESC';
  dateFrom?: string;
  dateTo?: string;
  module?: string;
  propertyId?: number | string;
}

export interface GeneralEnquiryListResult {
  items: GeneralEnquiry[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

function extractFilename(contentDisposition: string | undefined): string | null {
  if (!contentDisposition) return null;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const match = contentDisposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] ?? null;
}

const EXPORT_MIME: Record<GeneralEnquiryExportFormat, string> = {
  csv: 'text/csv',
  excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
};

function extensionFor(format: GeneralEnquiryExportFormat): string {
  if (format === 'csv') return 'csv';
  if (format === 'pdf') return 'pdf';
  return 'xlsx';
}

export interface GeneralEnquiryCreateInput {
  name: string;
  email: string;
  phone?: string;
  message: string;
  subscribe_to_updates?: 'yes' | 'no' | boolean;
  agree_to_terms?: boolean;
  propertyId?: number | string;
}

export interface VerifyRecaptchaInput {
  token: string;
  propertyId?: number | string;
}

export interface VerifyRecaptchaResponse {
  success: boolean;
  score?: number;
  action?: string;
  challengeTs?: string;
  hostname?: string;
  propertyId?: number | string | null;
  property?: {
    id: number;
    reapitId: string;
    title: string;
  } | null;
}

export const generalEnquiryApi = {
  async list(params: GeneralEnquiryListParams = {}): Promise<GeneralEnquiryListResult> {
    const response = await apiClient.get<BackendApiResponse<EnquiryListResponse>>(
      API_ENDPOINTS.generalEnquiry.list,
      {
        params,
        skipSuccessToast: true,
        skipErrorToast: true,
      },
    );

    const data = response?.data?.data;
    return {
      items: data?.items ?? [],
      page: data?.page ?? 1,
      limit: data?.limit ?? 10,
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 0,
    };
  },

  async verifyRecaptcha(input: VerifyRecaptchaInput): Promise<VerifyRecaptchaResponse> {
    const response = await apiClient.post<BackendApiResponse<VerifyRecaptchaResponse>>(
      API_ENDPOINTS.generalEnquiry.verifyRecaptcha,
      input,
      { skipSuccessToast: true, skipErrorToast: true },
    );
    return response.data.data;
  },

  async create(input: GeneralEnquiryCreateInput): Promise<GeneralEnquiry> {
    const response = await apiClient.post<BackendApiResponse<GeneralEnquiry>>(
      API_ENDPOINTS.generalEnquiry.create,
      input,
      { skipSuccessToast: true, skipErrorToast: true },
    );
    return response.data.data;
  },

  async export(params: GeneralEnquiryExportParams): Promise<{
    blob: Blob;
    filename: string;
    rowCount: number;
  }> {
    const response = await apiClient.post<Blob>(API_ENDPOINTS.generalEnquiry.export, params, {
      responseType: 'blob',
      skipSuccessToast: true,
      skipErrorToast: true,
    });

    const rawBlob = response.data;
    // Force the correct MIME so blob: URLs trigger download, not inline render.
    const blob =
      rawBlob.type === EXPORT_MIME[params.type]
        ? rawBlob
        : new Blob([rawBlob], { type: EXPORT_MIME[params.type] });
    const filename =
      extractFilename(response.headers['content-disposition']) ??
      `general-enquiries-${new Date().toISOString().slice(0, 10)}.${extensionFor(params.type)}`;
    const rowCount = Number(response.headers['x-export-row-count']) || 0;

    return { blob, filename, rowCount };
  },
};
